import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";
import SubHeader from "./SubHeader";
import DataTable, { formatNum } from "./DataTable";
import TableLoader from "../TableLoader";
import Apiauth from "../../../API/Apiauth";
import SalesPersonChart from "./graph/SalesPersonChart";
import { toFormatPrice } from "../helpers/function";

function toLocationRows(row = {}) {
  const candidate =
    row.locations ||
    row.location_data ||
    row.location_wise ||
    row.locationWise ||
    row.location_list ||
    row.location_details ||
    [];

  if (!Array.isArray(candidate)) return [];

  return candidate.map((loc, idx) => ({
    _id: `${loc.location_id || loc.location_name || loc.location || "loc"}-${idx}`,
    locationName:
      loc.location_name || loc.location_Name || loc.location || "Location",
    target: loc.target,
    salesAmount: loc.sales_amount ?? loc.salesAmount,
    achievement: loc.achievement_pct ?? loc.achievement,
    qty: loc.qty,
    invoices: loc.invoices,
    customer: loc.customers ?? loc.customer,
  }));
}

function mapRows(apiData = []) {
  return apiData.map((row, idx) => ({
    _rowKey: String(
      row.salesperson_id || row.salesperson_name || `salesperson-${idx}`,
    ),
    salesPersonName: row.salesperson_name,
    target: row.target,
    salesAmount: row.sales_amount,
    achievement: row.achievement_pct,
    qty: row.qty,
    invoices: row.invoices,
    customer: row.customers,
    locations: toLocationRows(row),
  }));
}

function mapTotals(apiTotals = {}) {
  return {
    target: apiTotals.target,
    salesAmount: apiTotals.sales_amount,
    achievement: apiTotals.achievement+'%',
    qty: apiTotals.qty,
    invoices: apiTotals.invoices,
    customer: apiTotals.customers,
  };
}

function mapMetrics(summary = {}) {
  return [
    { label: "Salesperson", value: formatNum(summary.total_salesperson) },
    { label: "Total Sales", value: toFormatPrice(Number(summary.total_sales || 0)) },
    {
      label: "Overachieving salesperson",
      value: formatNum(summary.overachieving_salesperson),
    },
    {
      label: "Underachieving salesperson",
      value: formatNum(summary.underachieving_salesperson),
    },
  ];
}

const COLUMNS = [
  { key: "_index", label: "#", align: "center", width: 40 },
  {
    key: "salesPersonName",
    label: "Sales person name",
    align: "left",
    width: 230,
  },
  { key: "target", label: "Target", align: "left", width: 150, isPriceColumn: true },
  {
    key: "salesAmount",
    label: "Sales Amount",
    align: "left",
    width: 180,
    isPriceColumn: true,
  },
  { key: "achievement", label: "%Achievement", align: "right", width: 120 },
  { key: "qty", label: "Qty", align: "right", width: 120 },
  { key: "invoices", label: "Invoices", align: "right", width: 100 },
  { key: "customer", label: "Customers", align: "right", width: 130 },
];

const DEFAULT_PAGE_SIZE = 10;
const CHART_MAX_LIMIT = 100000;
const SalesPerson = () => {
  const [rows, setRows] = useState([]);
  const [chartRows, setChartRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [filter, setFilter] = useState({});
  const [loading, setLoading] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showCharts, setShowCharts] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const fetchSalesPersonSumm = ({
    page: p = 1,
    limit: l = pageSize,
    ...rest
  } = {}) => {
    setLoading(true);
    Apiauth(
      { page: p, limit: l, ...rest },
      "Inventory/reports/salesBySalesperson",
    )
      .then((res) => {
        if (res?.code === 200) {
          setRows(mapRows(res.data));
          setTotals(mapTotals(res.totals));
          setMetrics(mapMetrics(res.summary));
          setFilter(res.filters ?? {});
          setTotalRecords(res?.pagination?.total ?? 0);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchChartReport = (filters = {}) => {
    setLoading(true);
    Apiauth(
      { page: 1, limit: CHART_MAX_LIMIT, ...filters },
      "Inventory/reports/salesBySalesperson",
    )
      .then((res) => {
        if (res?.code === 200) {
          setChartRows(mapRows(res.data));
        }
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    fetchSalesPersonSumm({ page: 1, limit: DEFAULT_PAGE_SIZE });
  }, []);
  useEffect(() => {
    if (showCharts) {
      fetchChartReport(appliedFilters);
    }
  }, [showCharts, appliedFilters]);

  function handleFetchData({ page: newPage, limit: newLimit }) {
    if (newLimit !== pageSize) setPageSize(newLimit);
    setPage(newPage);
    fetchSalesPersonSumm({ ...appliedFilters, page: newPage, limit: newLimit });
  }

  function handleFilterApply(filterParams = {}) {
    setAppliedFilters(filterParams);
    setPage(1);
    setExpandedRows({});
    fetchSalesPersonSumm({ ...filterParams, page: 1, limit: pageSize });
  }

  function toggleExpanded(rowKey) {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  }

  function renderMetricValue(key, value) {
    if (value == null || value === "") return "";
    if (key === "achievement") {
      const n = Number(value);
      if (!Number.isFinite(n)) return value;
      const color = n >= 100 ? "#2db7a3" : "#de5a58";
      return <span style={{ color }}>{`${n}%`}</span>;
    }
    if (key === "target" || key === "salesAmount") {
      return toFormatPrice(Number(value || 0));
    }
    return formatNum(value);
  }

  const displayRows = useMemo(() => {
    const flat = [];
    rows.forEach((row, idx) => {
      flat.push({
        ...row,
        _isChild: false,
        _parentIndex: idx + 1,
        _stripeIndex: idx,
      });
      if (expandedRows[row._rowKey] && Array.isArray(row.locations)) {
        row.locations.forEach((loc) => {
          flat.push({
            _rowKey: `${row._rowKey}-${loc._id}`,
            _parentKey: row._rowKey,
            _isChild: true,
            _parentIndex: idx + 1,
            _stripeIndex: idx,
            salesPersonName: loc.locationName,
            target: loc.target,
            salesAmount: loc.salesAmount,
            achievement: loc.achievement,
            qty: loc.qty,
            invoices: loc.invoices,
            customer: loc.customer,
          });
        });
      }
    });
    return flat;
  }, [rows, expandedRows]);

  function renderCell(col, row) {
    const hasLocations =
      Array.isArray(row.locations) && row.locations.length > 0;
    const isExpanded = !!expandedRows[row._rowKey];

    if (row._isChild) {
      if (col.key === "_index") return "";
      if (col.key === "salesPersonName") {
        return (
          <span
            style={{
              paddingLeft: 16,
              color: "#606060",
              fontSize: 14,
              fontFamily: "Segoe UI",
            }}
          >
            {row.salesPersonName}
          </span>
        );
      }
      return (
        <span
          style={{ color: "#606060", fontSize: 14, fontFamily: "Segoe UI" }}
        >
          {renderMetricValue(col.key, row[col.key])}
        </span>
      );
    }

    if (col.key === "_index") {
      return row._parentIndex || row._rowIndex;
    }

    if (col.key === "salesPersonName") {
      return (
        <div
          onClick={() => hasLocations && toggleExpanded(row._rowKey)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            cursor: hasLocations ? "pointer" : "default",
          }}
          title={hasLocations ? "Expand locations" : ""}
        >
          <span>{row.salesPersonName}</span>
          {hasLocations && (
            <span style={{ fontSize: 12, color: "#8b8b8b" }}>
              {isExpanded ? "▲" : "▼"}
            </span>
          )}
        </div>
      );
    }

    return renderMetricValue(col.key, row[col.key]);
  }

  return (
    <>
      <Header title="Sales by Salesperson" metrics={metrics} />
      <SubHeader
        filters={filter}
        showCharts={showCharts}
        setShowCharts={setShowCharts}
        onFilterApply={handleFilterApply}
      />
      {loading ? (
        <TableLoader />
      ) : showCharts ? (
        <SalesPersonChart rows={chartRows} />
      ) : (
        <DataTable
          columns={COLUMNS}
          data={displayRows}
          totals={totals}
          renderCell={renderCell}
          showSearch={true}
          showDateRange={true}
          pageLimit={pageSize}
          totalRecords={totalRecords}
          currentPage={page}
          onPageChange={setPage}
          fetchData={handleFetchData}
        />
      )}
    </>
  );
};

export default SalesPerson;
